import styles from './runsTable.module.scss'

export type BatchRunStatus = 'running' | 'complete' | 'failed'

/** Shape of a row from the `viewthru_batch` table. */
export interface BatchRun {
    id: string
    created_at: string | null
    /** Start of the current attempt. Resume moves this; `created_at` never does. */
    run_started_at?: string | null
    created_by: string | null
    finished_at: string | null
    /**
     * Worked seconds across every attempt. Null on rows written before the
     * backend started storing it — those still derive from timestamps.
     */
    duration_seconds?: number | null
    status: BatchRunStatus
    /**
     * Pre-processing step a running batch is on, before row counters mean
     * anything. Null once AffiliaTech processing starts.
     */
    phase?: string | null
    environment: 'staging' | 'production' | string | null
    filename: string | null
    origin?: string | null
    origin_id?: number | null
    total_count: number | null
    /** Rows written during seeding. Status polling sends this so the card can draw a bar. */
    seeded_count?: number | null
    processed_count: number | null
    invalid_count: number | null
    success_count: number | null
    duplicate_count?: number | null
    eligible_count?: number | null
    /** Entries in this run whose credit search listed Equifax. */
    equifax_count?: number | null
    /** Entries in this run whose credit search listed TransUnion. */
    transunion_count?: number | null
    /** Upload eligibility: identity-valid rows (Equifax-ready). */
    equifax_valid_count?: number | null
    /** Upload eligibility: identity-valid rows that also have Title (TransUnion-ready). */
    transunion_valid_count?: number | null
    pdf_pending_count: number | null
    blocked_count: number | null
    challenge_count: number | null
    error_count: number | null
    results_key: string | null
    error_message: string | null
    updated_at: string | null
    /** Last time the running worker reported in; drives the client stall timeout. */
    heartbeat_at?: string | null
}

/** Per-person outcome from the `viewthru_batch_entry` table. */
export interface BatchRowRecord {
    id: number
    batch_id: string
    client_id: number | null
    duplicate_of_client_id?: number | null
    /** Which client database the person was already held in. */
    duplicate_source?: string | null
    /** Resolved backend-side from introducerSelector — see duplicateSources.js. */
    duplicate_source_label?: string | null
    row_number: number
    client_reference: string | null
    firstname: string | null
    lastname: string | null
    dob: string | null
    postcode: string | null
    /** From the joined client record — blank when the spreadsheet had no email. */
    email?: string | null
    telephone?: string | null
    status: string
    provider_reference: string | null
    error_message: string | null
    result_json_key: string | null
    credit_report_key: string | null
    aml_report_key: string | null
    created_at: string | null
    /** Same rule Resume uses: artefacts + structured rows, not just a PDF. */
    complete?: boolean
    /** Whether the client / AML / agreement / industry-match rows exist. */
    records?: {
        client: boolean
        aml: boolean
        agreements: boolean
        matches: boolean
    }
}

export const count = (value: number | null | undefined) => (value === null || value === undefined ? 0 : value)

export const canResumeBatch = (run: BatchRun) => {
    if (run.status === 'running') return false
    if (run.status === 'failed') return true
    return run.status === 'complete' && (count(run.pdf_pending_count) > 0 || count(run.error_count) > 0)
}

/** Rows that have started processing — excludes people still waiting in the queue. */
export const isCompletedBatchRow = (row: BatchRowRecord) => row.status !== 'pending'

export const csvEscape = (value: string | number | null | undefined) => {
    if (value == null) return ''
    const text = String(value)
    return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

export const downloadCsv = (filename: string, csv: string) => {
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
}

const COMPLETED_ROW_CSV_COLUMNS: {
    header: string
    value: (row: BatchRowRecord) => string | number | null | undefined
}[] = [
    { header: 'row_number', value: (row) => row.row_number },
    { header: 'firstname', value: (row) => row.firstname },
    { header: 'lastname', value: (row) => row.lastname },
    { header: 'dob', value: (row) => row.dob },
    { header: 'email', value: (row) => row.email },
    { header: 'postcode', value: (row) => row.postcode },
    { header: 'client_reference', value: (row) => row.client_reference },
    { header: 'status', value: (row) => row.status },
    { header: 'duplicate_of_client_id', value: (row) => row.duplicate_of_client_id },
    { header: 'provider_reference', value: (row) => row.provider_reference },
    { header: 'credit_report_key', value: (row) => row.credit_report_key },
    { header: 'aml_report_key', value: (row) => row.aml_report_key },
    { header: 'result_json_key', value: (row) => row.result_json_key },
    { header: 'error_message', value: (row) => row.error_message },
]

export const buildCompletedRowsCsv = (rows: BatchRowRecord[]) => {
    const completed = rows.filter(isCompletedBatchRow).sort((a, b) => a.row_number - b.row_number)
    const lines = [
        COMPLETED_ROW_CSV_COLUMNS.map((column) => csvEscape(column.header)).join(','),
        ...completed.map((row) =>
            COMPLETED_ROW_CSV_COLUMNS.map((column) => csvEscape(column.value(row))).join(',')
        ),
    ]
    return lines.join('\n')
}

export const downloadCompletedRowsCsv = (rows: BatchRowRecord[], filenameBase: string) => {
    const completed = rows.filter(isCompletedBatchRow)
    if (!completed.length) return 0

    const safeName = String(filenameBase || 'viewthru-batch')
        .replace(/[^\w.-]+/g, '_')
        .replace(/^_+|_+$/g, '')
    downloadCsv(`${safeName || 'viewthru-batch'}-completed.csv`, buildCompletedRowsCsv(rows))
    return completed.length
}

export const formatDateTime = (raw: string | null | undefined) => {
    if (!raw) return '—'
    try {
        return new Date(raw).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    } catch {
        return raw
    }
}

/** Shared by the run history table and the live labels on the running card. */
export const formatSeconds = (totalSeconds: number) => {
    const seconds = Math.max(0, Math.round(totalSeconds))
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m ${seconds % 60}s`
}

/**
 * End timestamp wins. `now` is only for a still-running batch — omit it once
 * the run has finished so a missing end does not keep ticking.
 */
export const formatDuration = (start: string | null, end: string | null, now?: number) => {
    if (!start) return '—'
    const endMs = end ? new Date(end).getTime() : now
    if (endMs == null || !Number.isFinite(endMs)) return '—'
    const ms = endMs - new Date(start).getTime()
    if (!Number.isFinite(ms) || ms < 0) return '—'
    return formatSeconds(ms / 1000)
}

/**
 * Prefers the banked `duration_seconds` so a resume does not count the idle
 * gap since upload. Falls back to timestamps until that column exists.
 */
export const formatRunDuration = (
    run: Pick<
        BatchRun,
        'created_at' | 'run_started_at' | 'finished_at' | 'updated_at' | 'status' | 'duration_seconds'
    >,
    now: number = Date.now()
) => {
    const banked = run.duration_seconds
    if (typeof banked === 'number' && Number.isFinite(banked)) {
        if (run.status === 'running') {
            const start = run.run_started_at || run.created_at
            const extra = start ? Math.max(0, (now - new Date(start).getTime()) / 1000) : 0
            return formatSeconds(banked + extra)
        }
        return formatSeconds(banked)
    }
    if (run.status === 'running') {
        return formatDuration(run.created_at, run.finished_at, now)
    }
    return formatDuration(run.created_at, run.finished_at || run.updated_at)
}

export const StatusBadge = ({ status }: { status: BatchRunStatus }) => {
    const className =
        status === 'running'
            ? styles.statusRunning
            : status === 'complete'
            ? styles.statusComplete
            : styles.statusFailed
    return (
        <span className={`${styles.badge} ${className}`}>
            {status === 'running' && <span className={styles.statusSpinner} aria-hidden="true" />}
            {status}
        </span>
    )
}

export const EnvironmentBadge = ({ run }: { run: BatchRun }) => {
    if (!run.environment) {
        return <span className={styles.badge}>—</span>
    }
    const isProduction = run.environment === 'production'
    return (
        <span className={`${styles.badge} ${isProduction ? styles.envProduction : styles.envStaging}`}>
            {isProduction ? 'Production' : 'Staging'}
        </span>
    )
}

/** Outcomes the runner writes to `viewthru_batch_entry.status`. */
const ROW_STATUS_LABELS: Record<string, string> = {
    pending: 'Waiting',
    blocked: 'Blocked',
    challenge: 'Challenged identity',
    invalid: 'Invalid row',
    error: 'Error',
}

export const RowStatusBadge = ({ row }: { row: BatchRowRecord }) => {
    const { status } = row
    if (status === 'pending') {
        return <span className={`${styles.badge} ${styles.statusRunning}`}>{ROW_STATUS_LABELS.pending}</span>
    }
    if (status === 'duplicate') {
        return (
            <span
                className={`${styles.badge} ${styles.statusDuplicate}`}
                title={
                    row.duplicate_source
                        ? `Already held in ${row.duplicate_source_label || row.duplicate_source}`
                        : row.duplicate_of_client_id
                          ? `Already checked — reusing client ${row.duplicate_of_client_id}`
                          : 'Already checked earlier in this file'
                }
            >
                Skipped duplicate
            </span>
        )
    }
    if (status === 'invalid' || status === 'blocked' || status === 'challenge' || status === 'error') {
        return <span className={`${styles.badge} ${styles.statusFailed}`}>{ROW_STATUS_LABELS[status] || status}</span>
    }

    const complete = row.complete ?? status === 'success'
    return (
        <span className={`${styles.badge} ${complete ? styles.statusComplete : styles.statusRunning}`}>
            {complete ? 'Complete' : 'Incomplete'}
        </span>
    )
}

type BatchSummaryField = { label: string; value: (run: BatchRun) => string }

/** Count with % of completed searches; em dash when there is nothing to score against. */
export const formatBureauCoverage = (bureauCount: number | null | undefined, completeCount: number | null | undefined) => {
    const bureau = count(bureauCount)
    const complete = count(completeCount)
    if (complete <= 0 && bureau <= 0) return '—'
    if (complete <= 0) return String(bureau)
    const pct = Math.round((bureau / complete) * 1000) / 10
    const pctLabel = Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1)}%`
    return `${bureau.toLocaleString('en-GB')} (${pctLabel})`
}

/** What someone checks first: did everyone get through, and what is left. */
export const BATCH_SUMMARY_PRIMARY_FIELDS: BatchSummaryField[] = [
    { label: 'Rows in file', value: (run) => String(count(run.total_count)) },
    { label: 'Rows sent', value: (run) => String(count(run.processed_count)) },
    { label: 'Complete', value: (run) => String(count(run.success_count)) },
    { label: 'Duplicates', value: (run) => String(count(run.duplicate_count)) },
    { label: 'Eligible', value: (run) => (run.eligible_count == null ? '—' : String(count(run.eligible_count))) },
    { label: 'PDFs pending', value: (run) => String(count(run.pdf_pending_count)) },
    { label: 'Errors', value: (run) => String(count(run.error_count)) },
    { label: 'Duration', value: (run) => formatRunDuration(run) },
]

/** Rendered as progress rings ahead of the numeric primary cards. */
export const BATCH_SUMMARY_BUREAU_FIELDS = [
    { label: 'Equifax', key: 'equifax_count' as const },
    { label: 'TransUnion', key: 'transunion_count' as const },
] as const

export const BATCH_SUMMARY_SECONDARY_FIELDS: BatchSummaryField[] = [
    { label: 'Batch ID', value: (run) => run.id },
    { label: 'Started', value: (run) => formatDateTime(run.created_at) },
    { label: 'Started by', value: (run) => run.created_by || '—' },
    { label: 'Account', value: (run) => (run.environment === 'production' ? 'Production' : 'Staging') },
    { label: 'Invalid rows', value: (run) => String(count(run.invalid_count)) },
    { label: 'Blocked', value: (run) => String(count(run.blocked_count)) },
    { label: 'Challenged identity', value: (run) => String(count(run.challenge_count)) },
]
